import { ratingOptions, regions, years } from '@/mocks/movies';

export interface Filters {
  region: string;
  year: string | number;
  minRating: number;
  sort: string;
}

const sortOptions = [
  { key: 'new', label: '最新上线', icon: 'ri-time-line' },
  { key: 'hot', label: '热度最高', icon: 'ri-fire-line' },
  { key: 'rating', label: '评分最高', icon: 'ri-star-line' },
];

const chipBase =
  'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors cursor-pointer border';

interface FilterPanelProps {
  filters: Filters;
  onChange: (next: Partial<Filters>) => void;
  onReset: () => void;
  activeCount: number;
}

export function FilterChips({
  label,
  values,
  current,
  onSelect,
}: {
  label: string;
  values: (string | number)[];
  current: string | number;
  onSelect: (value: string | number) => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 py-3 border-b border-background-200 last:border-b-0">
      <span className="w-16 shrink-0 text-xs text-foreground-500 pt-1.5">{label}</span>
      <div className="flex flex-wrap gap-2">
        {values.map((value) => (
          <button
            key={String(value)}
            type="button"
            onClick={() => onSelect(value)}
            className={`${chipBase} ${
              current === value
                ? 'bg-primary-500 border-primary-500 text-background-50'
                : 'bg-background-50 border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            {value === '全部' ? '全部' : label === '评分' ? `${value} 分以上` : String(value)}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterPanel({ filters, onChange, onReset, activeCount }: FilterPanelProps) {
  return (
    <div className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-background-200 bg-background-100">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-filter-3-line text-sm"></i>
          </span>
          <h3 className="font-heading text-sm font-bold text-foreground-950">内容筛选</h3>
          {activeCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 text-[10px] font-bold">
              {activeCount}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-foreground-500 hover:text-primary-600 flex items-center gap-1 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i> 重置
        </button>
      </div>

      <div className="px-4 py-1">
        <FilterChips
          label="地区"
          values={regions}
          current={filters.region}
          onSelect={(value) => onChange({ region: String(value) })}
        />
        <FilterChips
          label="年份"
          values={years}
          current={filters.year}
          onSelect={(value) => onChange({ year: value })}
        />
        <FilterChips
          label="评分"
          values={ratingOptions.map((item) => item.value)}
          current={filters.minRating}
          onSelect={(value) => onChange({ minRating: Number(value) })}
        />
      </div>

      <div className="px-4 py-3 border-t border-background-200">
        <p className="text-[11px] text-foreground-500 leading-relaxed">
          <i className="ri-information-line mr-1"></i>
          评分与热度为演示数据，用于展示筛选与排序效果。
        </p>
      </div>
    </div>
  );
}

export function SortBar({
  sort,
  onChange,
  total,
}: {
  sort: string;
  onChange: (value: string) => void;
  total: number;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
      <p className="text-xs md:text-sm text-foreground-600">
        共找到 <span className="font-bold text-primary-600 font-label">{total}</span> 部作品
      </p>
      <div className="flex items-center gap-1 bg-background-100 border border-background-200 rounded-full p-1 w-fit">
        {sortOptions.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => onChange(option.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors whitespace-nowrap cursor-pointer ${
              sort === option.key
                ? 'bg-background-50 text-primary-600 border border-primary-200'
                : 'text-foreground-600 hover:text-primary-600 border border-transparent'
            }`}
          >
            <i className={`${option.icon} text-sm`}></i>
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}